/**
 * RealRealtimeService - Production implementation of IRealtimeService
 *
 * Wraps the legacy realtime API infrastructure for sending messages.
 * This is a thin adapter layer that allows coordinators to send messages
 * without directly importing the legacy API code.
 */

import type { IRealtimeService } from './IRealtimeService';
import type { WebsocketMessage } from '@playcastdotio/Messaging';
import {
  sendMessage as legacySendMessage,
  onConnected as legacyOnConnected,
  onHostAuthSuccess as legacyOnHostAuthSuccess,
} from '../../api/modules/realtime';
import { attachSentryTraceToMessage } from '../../api/modules/realtime/tracing';

export class RealRealtimeService implements IRealtimeService {
  /**
   * Send a message to the realtime API
   * Automatically attaches Sentry trace for debugging
   */
  sendMessage(message: WebsocketMessage): void {
    // Attach Sentry trace for debugging
    const tracedMessage = attachSentryTraceToMessage(message);

    // Send via legacy infrastructure
    legacySendMessage(tracedMessage);
  }

  /**
   * Check if connected to realtime API
   *
   * TODO: The legacy realtime module doesn't expose connection state.
   * For now, we assume connected. In the future, we should add a proper
   * connection state export from the realtime module.
   */
  isConnected(): boolean {
    // Legacy code doesn't expose connection state
    // Return true for now (messages will fail gracefully if not connected)
    return true;
  }

  /**
   * Register a listener for connection events (connection and reconnection)
   */
  onConnected(listener: () => void): () => void {
    return legacyOnConnected(listener);
  }

  onHostAuthSuccess(listener: () => void): () => void {
    return legacyOnHostAuthSuccess(listener);
  }
}
